/**
 * Email helper para cotizaciones — genera un fichero `.eml` con cuerpo
 * HTML (tabla bonita) y la cabecera mágica `X-Unsent: 1` que hace que
 * Outlook lo abra como BORRADOR EDITABLE en lugar de como mensaje recibido.
 *
 * Limitaciones conocidas:
 *  - Sólo Outlook (clásico o nuevo) honra X-Unsent: 1.
 *  - El From: es un hint; Outlook usa el perfil activo.
 */
const REMITENTE_DEFAULT = 'jesus@approx.es';

export type CotizMailItem = {
  producto_nombre?: string | null;
  nombre_propuesto?: string | null;
  ref_proveedor?: string | null;
  subcategoria_nombre?: string | null;
  detalle_nombre?: string | null;
  unidades?: number | null;
  coste_game_final_eur?: number | string | null;
  main_specs?: string | null;
};

function fmtEur(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
}

function safe(s: string | null | undefined): string {
  const t = (s ?? '').toString().trim();
  return t || '—';
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function encodeHeader(s: string): string {
  // ¿ASCII puro (code points 0-127)? Si lo es, no hace falta codificar (RFC 2047).
  if ([...s].every((c) => c.charCodeAt(0) <= 0x7f)) return s;
  const utf8 = unescape(encodeURIComponent(s));
  return `=?UTF-8?B?${btoa(utf8)}?=`;
}

function buildSubject(items: CotizMailItem[]): string {
  if (items.length === 1) {
    const it = items[0];
    const nombre = safe(it.producto_nombre || it.nombre_propuesto || it.ref_proveedor);
    return `Cotización Approx — ${nombre}`;
  }
  return `Cotización Approx — ${items.length} productos`;
}

function sortItems(items: CotizMailItem[]): CotizMailItem[] {
  return [...items].sort((a, b) => {
    const tipoA = [a.subcategoria_nombre, a.detalle_nombre].filter(Boolean).join(' — ');
    const tipoB = [b.subcategoria_nombre, b.detalle_nombre].filter(Boolean).join(' — ');
    const cmp = tipoA.localeCompare(tipoB, 'es');
    if (cmp !== 0) return cmp;
    return (Number(a.coste_game_final_eur) || 0) - (Number(b.coste_game_final_eur) || 0);
  });
}

function buildHtmlBody(items: CotizMailItem[]): string {
  const filas = sortItems(items).map((it) => {
    const nombre = escapeHtml(safe(it.producto_nombre || it.nombre_propuesto));
    const tipo = escapeHtml([it.subcategoria_nombre, it.detalle_nombre].filter(Boolean).join(' — ') || '—');
    const uds = it.unidades != null ? String(it.unidades) : '—';
    const coste = escapeHtml(fmtEur(it.coste_game_final_eur));
    const specs = escapeHtml(safe(it.main_specs));
    return `
      <tr>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;vertical-align:top;">${nombre}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;vertical-align:top;">${tipo}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;vertical-align:top;text-align:right;font-variant-numeric:tabular-nums;">${uds}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;vertical-align:top;text-align:right;font-weight:600;font-variant-numeric:tabular-nums;white-space:nowrap;">${coste}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;vertical-align:top;color:#374151;font-size:13px;">${specs}</td>
      </tr>`;
  }).join('');

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<title>Cotización Approx</title>
</head>
<body style="margin:0;padding:24px;background:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.55;color:#1f2937;">
  <p style="margin:0 0 12px 0;">Hola,</p>
  <p style="margin:0 0 18px 0;">Te paso cotización para los siguientes productos:</p>

  <table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;border:1px solid #d1d5db;width:100%;max-width:1080px;font-size:14px;">
    <thead>
      <tr style="background:#f3f4f6;">
        <th align="left"  style="padding:10px;border-bottom:2px solid #d1d5db;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569;width:22%;">Producto</th>
        <th align="left"  style="padding:10px;border-bottom:2px solid #d1d5db;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569;width:20%;">Tipo producto</th>
        <th align="right" style="padding:10px;border-bottom:2px solid #d1d5db;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569;width:8%;">MOQ</th>
        <th align="right" style="padding:10px;border-bottom:2px solid #d1d5db;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569;width:12%;white-space:nowrap;">P.Coste</th>
        <th align="left"  style="padding:10px;border-bottom:2px solid #d1d5db;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569;width:38%;">Main specs</th>
      </tr>
    </thead>
    <tbody>${filas}
    </tbody>
  </table>

  <p style="margin:18px 0 0 0;">Quedamos a la espera de tu confirmación.</p>
  <p style="margin:18px 0 0 0;">
    Saludos,<br/>
    <strong>Jesús Adán</strong><br/>
    <span style="color:#6b7280;font-size:13px;">Approx</span>
  </p>
</body>
</html>`;
}

function buildPlainBody(items: CotizMailItem[]): string {
  const filas = sortItems(items).map((it) => {
    const tipo = [it.subcategoria_nombre, it.detalle_nombre].filter(Boolean).join(' — ') || '—';
    return [
      safe(it.producto_nombre || it.nombre_propuesto),
      tipo,
      it.unidades != null ? String(it.unidades) : '—',
      fmtEur(it.coste_game_final_eur),
      safe(it.main_specs),
    ].join('\t');
  }).join('\r\n');
  return [
    'Hola,',
    '',
    'Te paso cotización para los siguientes productos:',
    '',
    ['Producto', 'Tipo producto', 'MOQ', 'P.Coste', 'Main specs'].join('\t'),
    filas,
    '',
    'Quedamos a la espera de tu confirmación.',
    '',
    'Saludos,',
    'Jesús Adán — Approx',
  ].join('\r\n');
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function wrapBase64(b64: string, lineLen = 76): string {
  const lines: string[] = [];
  for (let i = 0; i < b64.length; i += lineLen) lines.push(b64.slice(i, i + lineLen));
  return lines.join('\r\n');
}

export type EmlAttachment = {
  filename: string;
  mimeType: string;
  data: ArrayBuffer;
};

function buildEml(items: CotizMailItem[], opts?: { from?: string; to?: string | null; attachment?: EmlAttachment }): string {
  const from = opts?.from || REMITENTE_DEFAULT;
  void opts?.to;
  const to = '';
  const subject = buildSubject(items);
  const html = buildHtmlBody(items);
  const text = buildPlainBody(items);
  const date = new Date().toUTCString();

  const altBoundary = `=_alt_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  const mixedBoundary = `=_mix_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

  const altPart = [
    `--${altBoundary}`,
    'Content-Type: text/plain; charset=UTF-8',
    'Content-Transfer-Encoding: 8bit',
    '',
    text,
    '',
    `--${altBoundary}`,
    'Content-Type: text/html; charset=UTF-8',
    'Content-Transfer-Encoding: 8bit',
    '',
    html,
    '',
    `--${altBoundary}--`,
  ].join('\r\n');

  if (!opts?.attachment) {
    return [
      'X-Unsent: 1',
      `From: ${from}`,
      `To: ${to}`,
      `Subject: ${encodeHeader(subject)}`,
      `Date: ${date}`,
      'MIME-Version: 1.0',
      `Content-Type: multipart/alternative; boundary="${altBoundary}"`,
      '',
      altPart,
      '',
    ].join('\r\n');
  }

  const att = opts.attachment;
  const b64 = wrapBase64(arrayBufferToBase64(att.data));

  return [
    'X-Unsent: 1',
    `From: ${from}`,
    `To: ${to}`,
    `Subject: ${encodeHeader(subject)}`,
    `Date: ${date}`,
    'MIME-Version: 1.0',
    `Content-Type: multipart/mixed; boundary="${mixedBoundary}"`,
    '',
    `--${mixedBoundary}`,
    `Content-Type: multipart/alternative; boundary="${altBoundary}"`,
    '',
    altPart,
    '',
    `--${mixedBoundary}`,
    `Content-Type: ${att.mimeType}; name="${att.filename}"`,
    `Content-Disposition: attachment; filename="${att.filename}"`,
    'Content-Transfer-Encoding: base64',
    '',
    b64,
    '',
    `--${mixedBoundary}--`,
    '',
  ].join('\r\n');
}

export function downloadCotizEml(items: CotizMailItem[], opts?: { from?: string; to?: string | null; attachment?: EmlAttachment }): void {
  if (items.length === 0) return;
  const content = buildEml(items, opts);
  const blob = new Blob([content], { type: 'message/rfc822;charset=UTF-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = items.length === 1
    ? (items[0].ref_proveedor || items[0].nombre_propuesto || 'producto').replace(/[^a-z0-9-]+/gi, '-').slice(0, 40)
    : `${items.length}-productos`;
  a.download = `cotizacion-approx-${slug}-${new Date().toISOString().slice(0, 10)}.eml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export const openCotizMailto = downloadCotizEml;
